import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useListComparatorPreferencesQuery } from 'services/api/Meteorology';
import usePopularComparisons from 'utils/hooks/meteorology/usePopularComparisons';
import useSetComparison from 'utils/hooks/meteorology/comparator/useSetComparison';
import { setTriggerActivity } from 'redux/reducers/meteorologyFieldsComparatorControlPanelReducer';
import { defaultPopularComparison } from 'helper/Meteorology/Fields/Comparator/openDefaults';

/**
 * Default do Comparador (Fields) ao abrir sem preferência salva (#1822, item 5.2).
 *
 * Precedência: preferência salva > comparação popular default (ECMWF46).
 * A decisão é one-shot (`applied`): assim que a lista de preferências chega,
 * aplica a `preferences[0]` se houver; senão espera as comparações populares
 * carregarem e aplica o default do ECMWF46. Em qualquer caso dispara a consulta.
 * Se não há preferência nem popular, encerra sem despachar nada.
 */
export default function useApplyComparatorOpenDefault() {
  const dispatch = useDispatch();
  const { data } = useListComparatorPreferencesQuery();
  const { comparisons, isFetching } = usePopularComparisons();
  const setComparison = useSetComparison();

  const applied = useRef(false);

  useEffect(() => {
    if (applied.current) return;
    // Espera as preferências resolverem antes de decidir.
    if (data === undefined) return;

    const preferences = data.data || [];
    if (preferences.length > 0) {
      applied.current = true;
      setComparison(preferences[0], 'Preferências Salvas');
      dispatch(setTriggerActivity(true));
      return;
    }

    // Sem preferência salva: cai para a comparação popular default, mas só
    // depois que a lista de populares carregar.
    if (isFetching) return;
    applied.current = true;

    const fallback = defaultPopularComparison(comparisons);
    if (!fallback) return;
    setComparison(fallback, 'Comparações Populares');
    dispatch(setTriggerActivity(true));
  }, [data, comparisons, isFetching, setComparison, dispatch]);
}
